import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ClipboardCheck,
  Plus,
  Search,
  Loader2,
  LayoutGrid,
  List,
} from 'lucide-react';
import { useIsCustomer } from '../hooks/usePortalMode';
import { useQualificationsData } from './supplier-qualifications/useQualificationsData';
import ListView from './supplier-qualifications/ListView';
import MatrixView from './supplier-qualifications/MatrixView';
import AssignDialog from './supplier-qualifications/AssignDialog';

export default function SupplierQualifications() {
  const isCustomer = useIsCustomer();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'matrix'>('list');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Pagination state for supplier rows
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // Assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const { qualifications, loading, templates, suppliers, suppliersLoading, loadQualifications } =
    useQualificationsData(filterStatus, isCustomer, showAssignDialog);

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to page 1 on search
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const filteredQualifications = qualifications.filter(qual =>
    qual.template_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    qual.supplier_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    qual.customer_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Reset to page 1 when status filter changes (search reset is handled in debounce effect)
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading questionnaires...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Main Card Container - matches Quality page layout */}
      <Card className="rounded-lg shadow-sm">
        {/* Action Header */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-end">
            {isCustomer && (
              <Button onClick={() => setShowAssignDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Assign Questionnaire
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Filters Toolbar */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search questionnaires..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="invited">Invited</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="more_info_required">More Info Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-1 border rounded-md p-1 bg-muted/30">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-8 px-3"
                >
                  <List className="h-4 w-4 mr-1" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'matrix' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('matrix')}
                  className="h-8 px-3"
                >
                  <LayoutGrid className="h-4 w-4 mr-1" />
                  Matrix
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {filteredQualifications.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No questionnaires found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : isCustomer
                    ? 'Get started by assigning a questionnaire to a supplier'
                    : 'No questionnaires have been assigned to you yet'}
                </p>
                {isCustomer && !searchTerm && filterStatus === 'all' && (
                  <Button onClick={() => setShowAssignDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Assign Questionnaire
                  </Button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              <ListView filteredQualifications={filteredQualifications} />
            ) : (
              <MatrixView
                filteredQualifications={filteredQualifications}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assign Qualification Dialog */}
      <AssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        templates={templates}
        suppliers={suppliers}
        suppliersLoading={suppliersLoading}
        onAssigned={loadQualifications}
      />
    </div>
  );
}
